import { Check, Crown } from "lucide-react";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { PaywallButton } from "@/components/PaywallButton";
import { CreatorCodeLink } from "@/components/monetization/CreatorCodeLink";
import { useAuth } from "@/contexts/AuthContext";

const BENEFITS = [
  "Unlimited AI workouts built around your recovery",
  "Full multi-week training plans",
  "AI nutrition + Apple Health insights",
  "Trending workouts, shoutouts & deeper analytics",
];

interface UpgradeSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Analytics label for where this came from */
  feature: string;
  title?: string;
  subtitle?: string;
}

/**
 * Pre-paywall sheet: sells the value first, then opens the native paywall.
 * Logs a real "viewed" only when the sheet is actually opened.
 */
export function UpgradeSheet({ open, onOpenChange, feature, title, subtitle }: UpgradeSheetProps) {
  const { user } = useAuth();
  if (!user) return null;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="pb-[calc(var(--safe-area-bottom,0px)+1rem)]">
        <div className="mx-auto w-full max-w-md px-5">
          <DrawerHeader className="px-0 text-center">
            <div className="mx-auto mb-3 w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center shadow-lg shadow-primary/30">
              <Crown className="w-7 h-7 text-primary-foreground" />
            </div>
            <DrawerTitle className="text-2xl font-display">
              {title ?? "Train smarter with Pro"}
            </DrawerTitle>
            <DrawerDescription className="text-sm">
              {subtitle ?? "Try every Pro feature free for 7 days."}
            </DrawerDescription>
          </DrawerHeader>

          <ul className="space-y-2.5 mb-5">
            {BENEFITS.map((b) => (
              <li key={b} className="flex items-start gap-2.5 text-sm">
                <span className="mt-0.5 w-5 h-5 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                  <Check className="w-3 h-3 text-primary" />
                </span>
                <span>{b}</span>
              </li>
            ))}
          </ul>

          <div className="rounded-2xl border border-primary/20 bg-primary/5 p-3 mb-4 text-center">
            <p className="text-sm font-semibold">$0 today · 7 days free</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              We'll remind you before it ends. Cancel anytime in Settings.
            </p>
          </div>

          {open && (
            <PaywallButton
              externalId={user.id}
              offering="lovable_app"
              variant="glow"
              size="lg"
              className="w-full"
              feature={feature}
              onComplete={() => onOpenChange(false)}
            >
              Start My Free Week
            </PaywallButton>
          )}
          <div className="flex justify-center mt-2">
            <CreatorCodeLink />
          </div>
          <Button variant="ghost" className="w-full mt-1 text-muted-foreground" onClick={() => onOpenChange(false)}>
            Not now
          </Button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
